import { native } from '@forest/native-services'
import {
  createResource,
  Match,
  onMount,
  Show,
  Switch,
  type JSX,
} from 'solid-js'
import { Motion } from 'solid-motionone'
import { CountdownTiles } from '~/components/countdown-tiles'
import { CheckCutoutIcon } from '~/components/icons/check-cutout'
import { ClockIcon } from '~/components/icons/clock'
import { CloseIcon } from '~/components/icons/close'
import { SpecialProgressBar } from '~/components/special-progress-bar'
import { Spinner } from '~/components/spinner'
import { createCountdown } from '~/lib/countdown'
import { getLocalizedMessage, type L10nParam } from '~/lib/l10n'
import { specialTimerText } from '~/lib/special-challenge-timer'
import { m } from '~/paraglide/messages'
import type {
  SpecialChallengeConfig,
  VisibleSpecialChallengeCard,
} from '~/resources/special-challenge'

type Props = {
  card: VisibleSpecialChallengeCard
  config: SpecialChallengeConfig
  claiming: boolean
  onClaim: (event: MouseEvent) => void
  onClose: () => void
}

// A copy miss must degrade, never take the page down: a rejected bridge call
// (not just an echoed key) resolves to null instead of putting the Solid
// resource into its error state, which would throw into the app-level
// ErrorBoundary on read.
function getLocalizedMessageOrNull(key: string, params?: L10nParam[]) {
  return getLocalizedMessage(key, params).catch(() => null)
}

// The full-screen view of the special challenge, opened from the entry card.
// It is view state over the hub — no URL, no history entry — and mirrors the
// card's state machine: pending (upcoming), active, and grace all render from
// the same derived card. See CONTEXT.md#special-challenge-detail-view-限時挑戰詳細頁.
export function SpecialChallengeDetail(props: Props) {
  // Analytics counts openings as page views (the name does not make this a
  // page — still URL-less view state): every open mounts fresh, so re-opens
  // re-fire, in any state (pending, active, grace).
  onMount(() => {
    void native.general.logEvent({
      event_name: 'page_view',
      event_params: { page_name: 'special_challenge_main_page' },
    })
  })

  const countdown = createCountdown(() => props.card.countdownTo)
  const barCard = () => (props.card.kind === 'upcoming' ? null : props.card)

  // Per-event copy resolves from the native string catalog; a miss degrades
  // (generic title, no description line) and never blocks the claim.
  const [eventTitle] = createResource(
    () => props.config.main_page_title_key,
    (key) => getLocalizedMessageOrNull(key),
  )
  const [rewardName] = createResource(
    () => `tree_type_${props.config.reward_tree_type_gid}_title`,
    (key) => getLocalizedMessageOrNull(key),
  )
  const [description] = createResource(
    () => {
      const b = barCard()
      const name = rewardName()
      if (!b || !name) return null
      return {
        key: props.config.main_page_description_key,
        target: b.target,
        name,
      }
    },
    ({ key, target, name }) =>
      getLocalizedMessageOrNull(key, [
        { type: 'integer', value: String(target) },
        { type: 'string', value: name },
      ]),
  )

  return (
    <div
      class="fixed inset-0 z-30 overflow-y-auto overscroll-contain"
      style={{ background: props.config.main_page_bg }}
      data-kind={props.card.kind}
      data-bar={barCard()?.bar}
    >
      <div class="relative min-h-full w-full px-8 pb-[48px]">
        <div class="pointer-events-none absolute inset-x-0 top-0 h-[500px]">
          <img
            src={props.config.main_page_visual_url}
            alt=""
            draggable={false}
            class="h-full w-full object-cover"
          />
        </div>

        <div class="relative mx-auto flex w-full max-w-[640px] flex-col items-center gap-[22px] pt-[48vh]">
          <Switch>
            <Match when={props.card.kind === 'upcoming'}>
              <FadeIn
                order={1}
                class="flex w-full flex-col items-start gap-[8px] text-white"
              >
                <h1 class="text-[31px] font-bold leading-normal [text-shadow:0_0_16px_rgba(255,255,255,0.7)]">
                  {m.challenge_not_yet_started_title()}
                </h1>
                <p class="text-[17px] leading-[25px]">
                  {m.challenge_not_yet_started_description()}
                </p>
              </FadeIn>
              <FadeIn order={2} class="w-full">
                <CountdownTiles totalSeconds={countdown().totalSeconds} />
              </FadeIn>
            </Match>

            <Match when={barCard()}>
              {(b) => (
                <>
                  <FadeIn
                    order={1}
                    class="flex w-full flex-col items-start gap-[8px] text-white"
                  >
                    <h1 class="text-[31px] font-bold leading-normal [text-shadow:0_0_16px_rgba(255,255,255,0.7)]">
                      {eventTitle() ?? m.special_challenge_banner_title()}
                    </h1>
                    <Show when={description()}>
                      <p class="text-[17px] leading-[25px]">{description()}</p>
                    </Show>
                  </FadeIn>

                  <FadeIn
                    order={2}
                    class="flex w-full flex-col items-center gap-[20px]"
                  >
                    <div class="w-full rounded-[25px] bg-gradient-to-b from-white/60 to-white/30 to-[80%] px-[20px] pb-[20px] pt-[12px] shadow-[inset_0_4px_6px_0_rgba(255,255,255,0.3)]">
                      <div class="flex items-end">
                        <div class="-mr-[20px] min-w-0 flex-1 py-[8px]">
                          <SpecialProgressBar
                            progress={b().progress}
                            target={b().target}
                            trackColor={props.config.progress_bar_bg_color}
                            fillBg={props.config.progress_bg}
                          />
                        </div>
                        <img
                          src={props.config.reward_sticker_img_url}
                          alt=""
                          draggable={false}
                          class="relative size-[54px] shrink-0 object-contain"
                        />
                      </div>
                    </div>

                    <Show when={b().bar === 'claim'}>
                      <button
                        type="button"
                        disabled={props.claiming}
                        onClick={(e) => {
                          // Pure intent: counts the tap, not the claim
                          // outcome.
                          void native.general.logEvent({
                            event_name: 'component_event',
                            event_params: {
                              type: 'btn',
                              app_action: 'click',
                              component_name:
                                'special_challenge_main_page_claim',
                            },
                          })
                          props.onClaim(e)
                        }}
                        class="tap-highlight-none flex h-[52px] w-full items-center justify-center rounded-full text-[20px] font-semibold leading-[28px] text-white transition-transform duration-200 active:scale-[0.98]"
                        style={{ background: props.config.claim_reward_btn_bg }}
                      >
                        <Show
                          when={!props.claiming}
                          fallback={
                            <Spinner
                              variant="circle"
                              class="size-[24px] text-white"
                            />
                          }
                        >
                          {m.btn_claim_reward()}
                        </Show>
                      </button>
                    </Show>
                    <Show when={b().bar === 'complete'}>
                      <div
                        class="flex h-[52px] w-full items-center justify-center gap-[8px] rounded-full opacity-50"
                        style={{ background: props.config.claim_reward_btn_bg }}
                      >
                        <span class="text-[20px] font-semibold leading-[28px] text-white">
                          {m.monthlytask_claimed()}
                        </span>
                        <CheckCutoutIcon class="size-[20px]" />
                      </div>
                    </Show>

                    <div class="flex items-center justify-center gap-[3px] text-white opacity-80">
                      <ClockIcon class="size-[20px] shrink-0" />
                      <span class="whitespace-nowrap text-[14px] leading-[20px]">
                        {specialTimerText(
                          props.card.kind,
                          countdown().totalSeconds,
                        )}
                      </span>
                    </div>
                  </FadeIn>
                </>
              )}
            </Match>
          </Switch>
        </div>

        <div class="absolute inset-x-0 top-0 pt-[calc(2px_+_var(--safe-area-top))]">
          <div class="flex h-[44px] items-center px-[12px]">
            <button
              type="button"
              class="tap-highlight-none grid size-[40px] place-items-center rounded-full bg-[#0000004D] text-white"
              onClick={() => props.onClose()}
            >
              <CloseIcon class="size-[20px]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function FadeIn(props: {
  order: number
  class?: string
  children: JSX.Element
}) {
  return (
    <Motion.div
      class={props.class}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.15 * (props.order - 1) }}
    >
      {props.children}
    </Motion.div>
  )
}
